/**
 * Second Opinion access, two lifetime free questions, then PRO / paid expert SO.
 * Server entitlement on /api/second-opinion-ai/* and /api/payments/entitlements/second-opinion is authoritative.
 */

import { API_BASE } from '@/config/api';
import axios from 'axios';

const USAGE_STORAGE_KEY = 'byonco_second_opinion_usage_v2';
const ENTITLEMENT_STORAGE_KEY = 'byonco_second_opinion_entitlement_v1';

export const FREE_QUESTIONS_LIFETIME = 0;
export const FREE_ATTACHMENTS_LIFETIME = 0;

export function getUsage() {
  try {
    const stored = localStorage.getItem(USAGE_STORAGE_KEY);
    if (!stored) {
      return {
        questionsUsed: 0,
        attachmentUsed: 0,
        lastUpdated: new Date().toISOString(),
      };
    }
    const parsed = JSON.parse(stored);
    return {
      questionsUsed: parsed.questionsUsed || 0,
      attachmentUsed: parsed.attachmentUsed || 0,
      lastUpdated: parsed.lastUpdated || new Date().toISOString(),
    };
  } catch (e) {
    console.error('Error reading usage data:', e);
    return {
      questionsUsed: 0,
      attachmentUsed: 0,
      lastUpdated: new Date().toISOString(),
    };
  }
}

function saveUsage(usage) {
  try {
    localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify({
      ...usage,
      lastUpdated: new Date().toISOString(),
    }));
  } catch (e) {
    console.error('Error saving usage data:', e);
  }
}

/** @deprecated Daily reset removed, two free questions are lifetime. */
export function resetIfNewDay() {
  return false;
}

export function canAskQuestion() {
  return hasEntitlement();
}

export function canAttach() {
  return hasEntitlement();
}

export function recordQuestionUsed() {
  const usage = getUsage();
  saveUsage({
    ...usage,
    questionsUsed: usage.questionsUsed + 1,
  });
}

export function recordAttachmentUsed() {
  const usage = getUsage();
  saveUsage({
    ...usage,
    attachmentUsed: usage.attachmentUsed + 1,
  });
}

export function syncUsageFromServer({ free_used, free_remaining, entitled } = {}) {
  if (entitled) {
    setEntitlementActive('server');
  }
  if (typeof free_used === 'number') {
    const usage = getUsage();
    saveUsage({
      ...usage,
      questionsUsed: Math.max(usage.questionsUsed || 0, free_used),
    });
  } else if (typeof free_remaining === 'number') {
    const usage = getUsage();
    const used = Math.max(0, FREE_QUESTIONS_LIFETIME - free_remaining);
    saveUsage({
      ...usage,
      questionsUsed: Math.max(usage.questionsUsed || 0, used),
    });
  }
}

export async function fetchServerEntitlement() {
  try {
    const { data } = await axios.get(`${API_BASE}/payments/entitlements/second-opinion`, {
      withCredentials: true,
      timeout: 15000,
    });
    syncUsageFromServer(data);
    return data;
  } catch (e) {
    if (e?.response?.status === 401) {
      return null;
    }
    console.warn('SO entitlement fetch failed', e);
    return null;
  }
}

export function getEntitlement() {
  try {
    const stored = localStorage.getItem(ENTITLEMENT_STORAGE_KEY);
    if (!stored) {
      return {
        active: false,
        activatedAt: null,
        source: null,
        entitlementId: null,
      };
    }
    return JSON.parse(stored);
  } catch (e) {
    console.error('Error reading entitlement data:', e);
    return {
      active: false,
      activatedAt: null,
      source: null,
      entitlementId: null,
    };
  }
}

export function setEntitlementActive(source = 'razorpay', entitlementId = null) {
  try {
    const entitlement = {
      active: true,
      activatedAt: new Date().toISOString(),
      source,
      entitlementId,
    };
    localStorage.setItem(ENTITLEMENT_STORAGE_KEY, JSON.stringify(entitlement));
    return entitlement;
  } catch (e) {
    console.error('Error setting entitlement:', e);
    return null;
  }
}

export function hasEntitlement() {
  const entitlement = getEntitlement();
  return entitlement.active === true;
}

export function clearEntitlement() {
  try {
    localStorage.removeItem(ENTITLEMENT_STORAGE_KEY);
  } catch (e) {
    console.error('Error clearing entitlement:', e);
  }
}
